import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { User } from "@/types";
import { getCurrentUser, logoutUser, ssoLoginUser } from "@/services/auth-service";
import { mockAuthService } from "@/services/mock-sso-service";
import { accessLevelOf, type AccessLevel } from "@/lib/access";
import { isAuthError } from "@/lib/api-error";
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, clearTokens, storeTokens } from "@/api/auth-tokens";

// 개발 서버에서만 — .env.local의 VITE_MOCK_SSO가 운영 빌드에 박히지 않도록 DEV로 막아요
const IS_MOCK_SSO = import.meta.env.DEV && import.meta.env.VITE_MOCK_SSO === 'true';

// Startup session check: retry once after this long when the failure is not an auth failure
const STARTUP_RETRY_DELAY_MS = 5000;
const STARTUP_USER_REQUEST = { skipServerErrorRedirect: true, skipNotFoundRedirect: true };

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  // guest · pending · demand · supplier · internal (server-decided, vault "설계/권한 모델")
  accessLevel: AccessLevel;
  logout: () => void;
  handleSsoCallback: (ssoAccessToken: string, ssoRefreshToken: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Restore the session on startup. Only an auth failure (401/403 — the interceptor has already tried to
  // refresh once, and dispatched auth:logout if the session is really gone) drops the tokens. A network error,
  // timeout or 5xx says nothing about the session: keep the tokens, show the guest UI (user stays null),
  // and try once more after a few seconds (a reload retries as well). The startup call never redirects to
  // /server-error or /404 — a failed load must not hijack the page the visitor is on.
  useEffect(() => {
    if (!localStorage.getItem(ACCESS_TOKEN_KEY)) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const load = (retriesLeft: number) => {
      getCurrentUser(STARTUP_USER_REQUEST)
        .then((loaded) => {
          // Signed out (or dropped by the interceptor) while the request was in flight: do not bring the user back
          if (!cancelled && localStorage.getItem(ACCESS_TOKEN_KEY)) setUser(loaded);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (isAuthError(error)) {
            clearTokens();
            setUser(null);
          } else if (retriesLeft > 0) {
            retryTimer = setTimeout(() => {
              if (localStorage.getItem(ACCESS_TOKEN_KEY)) load(retriesLeft - 1);
            }, STARTUP_RETRY_DELAY_MS);
          }
        })
        .finally(() => {
          if (!cancelled) setIsLoading(false);
        });
    };
    load(1);

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, []);

  // Listen for SPA-friendly logout triggered by the axios interceptor when the session cannot be
  // renewed (the access token is refreshed silently first, see api/auth-tokens.ts — tokens live
  // only in localStorage, so a silent refresh needs no state change here).
  // We clear state here so ProtectedRoute redirects via <Navigate>, avoiding
  // a hard navigation that would fire beforeunload on /chat and trigger the
  // browser's "Leave site?" popup.
  useEffect(() => {
    const handleAuthLogout = () => {
      clearTokens();
      setUser(null);
    };
    window.addEventListener("auth:logout", handleAuthLogout);
    return () => window.removeEventListener("auth:logout", handleAuthLogout);
  }, []);

  const handleSsoCallback = useCallback(async (ssoAccessToken: string, _ssoRefreshToken: string) => {
    let tokens;
    let currentUser;
    if (IS_MOCK_SSO) {
      tokens = await mockAuthService.ssoLoginUser(ssoAccessToken);
      storeTokens(tokens.access_token, tokens.refresh_token);
      // A locally minted token (local-env/mint_tokens.py) is a real local account — read it from the API.
      currentUser = import.meta.env.VITE_MOCK_ACCESS_TOKEN
        ? await getCurrentUser()
        : await mockAuthService.getCurrentUser();
    } else {
      tokens = await ssoLoginUser(ssoAccessToken);
      storeTokens(tokens.access_token, tokens.refresh_token);
      currentUser = await getCurrentUser();
    }
    setUser(currentUser);
  }, []);

  const logout = useCallback(() => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (refreshToken) {
      // axios runs its request interceptor after this function returns, i.e. after the tokens are
      // cleared below — so send the bearer explicitly or the server never revokes the refresh token.
      const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;
      logoutUser(refreshToken, { headers }).catch(() => {});
    }
    clearTokens();
    setUser(null);
    navigate("/");
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    const currentUser = await getCurrentUser();
    setUser(currentUser);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: !!user,
      accessLevel: accessLevelOf(user),
      logout,
      handleSsoCallback,
      refreshUser,
    }),
    [user, isLoading, logout, handleSsoCallback, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
